import ClosurePanel from 'modules/Admin/ClosurePanel'
import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Android, Apple } from '@mui/icons-material'
import AccountPage from '../../components/AccountPage'
import api from 'utils/api'
import { passwordResetMessages } from 'utils/constants'
import * as userActions from 'modules/User/actions'
import ProfileImageUpload from './components/ProfileImageUpload'
import AccountPageIntro from '../../components/AccountPageIntro'

const AccountInformation = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const user = useSelector((state) => state.user)
  const readOnly = user.details.lifecycleStatus === 'SUSPENDED'
  const [submitting, setSubmitting] = useState(false)
  const [resettingPassword, setResettingPassword] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      firstName: user.details.firstName || '',
      lastName: user.details.lastName || '',
      phone: user.details.phone || '',
      address: user.details.address || '',
    },
  })

  const handleFormSubmit = useCallback((values) => {
    setSubmitting(true)
    api
      .post('/user/profile', values)
      .then(() => {
        enqueueSnackbar('Account information updated', { variant: 'success' })
        reset(values)
        dispatch(userActions.fetchUser())
      })
      .catch(() => {
        enqueueSnackbar('Cannot update account information', { variant: 'error' })
      })
      .finally(() => setSubmitting(false))
  }, [dispatch, enqueueSnackbar, reset])

  const handlePasswordReset = useCallback(() => {
    setResettingPassword(true)
    api
      .post('/auth/forgot-password', { email: user.details.email })
      .then(() => {
        enqueueSnackbar(passwordResetMessages.success, {
          autoHideDuration: 3000,
          variant: 'success',
        })
      })
      .catch(() => enqueueSnackbar(passwordResetMessages.error, { variant: 'error' }))
      .finally(() => setResettingPassword(false))
  }, [enqueueSnackbar, user.details.email])

  const handleProfileImageUploaded = useCallback(() => {
    dispatch(userActions.fetchUser())
  }, [dispatch])

  return (
    <AccountPage title="Profile">
      <Box px={{ xs: 2, md: 3 }} pb={5}>
        <AccountPageIntro>
          Manage your personal details and account settings.
        </AccountPageIntro>
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, maxWidth: 920 }}>
            <Typography variant="h6" mb={2}>Personal details</Typography>
            <ProfileImageUpload
              email={user.details.email}
              firstName={user.details.firstName}
              imageUrl={user.details.imageUrl}
              lastName={user.details.lastName}
              onUploaded={handleProfileImageUploaded}
            />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First name"
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName?.message}
                  {...register('firstName', { required: 'Enter your first name.' })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last name"
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName?.message}
                  {...register('lastName', { required: 'Enter your last name.' })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled
                  label="Email"
                  value={user.details.email || ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile phone (optional)"
                  error={Boolean(errors.phone)}
                  helperText={errors.phone?.message}
                  {...register('phone')}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6">Address</Typography>
            <TextField
              fullWidth
              label="Address (optional)"
              margin="normal"
              multiline
              minRows={2}
              {...register('address')}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={3}>
              {user.details.provider === 'hug' && (
                <Button
                  color="primary"
                  disabled={readOnly || resettingPassword}
                  onClick={handlePasswordReset}
                  type="button"
                  variant="contained"
                >
                  {resettingPassword ? 'Sending' : 'Reset password'}
                </Button>
              )}
              <Button
                disabled={readOnly || submitting || !isDirty}
                type="submit"
                variant="contained"
                color="primary"
              >
                {submitting ? 'Saving' : 'Save changes'}
              </Button>
            </Stack>
          </Paper>

          <Box mt={3} maxWidth={920}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              HiddenUnderGround on mobile
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button disabled startIcon={<Android />} variant="outlined">
                Android app — Coming soon
              </Button>
              <Button disabled startIcon={<Apple />} variant="outlined">
                iOS app — Coming soon
              </Button>
            </Stack>
          </Box>
        </form></fieldset>
        <ClosurePanel />
      </Box>
    </AccountPage>
  )
}

export default AccountInformation
